import { useRuntimeVariables } from 'app-kit'
import { useKestrel } from 'features/kestrelViewer'
import { useRolodexFileLS } from 'features/rolodex'
import { useMemo } from 'react'
import { KVPhoto, RolodexFileRef } from 'types'

import { PhotoManager } from './photoManager'

const PHOTO_FILE_LIMIT = 5000

const isImageFile = (path: string) => {
  const lowerPath = path.toLowerCase()
  return (
    lowerPath.endsWith('.png') ||
    lowerPath.endsWith('.jpg') ||
    lowerPath.endsWith('.jpeg')
  )
}

const extractImageFiles = (fileRefs: readonly RolodexFileRef[]) => {
  return fileRefs
    .filter((file: RolodexFileRef) => isImageFile(file.path))
    .map((file) => ({
      ...file,
      filename: file.path.split('/').pop() || '',
    }))
}

export const usePhotoFiles = (
  entryId: string | undefined
): {
  photos: KVPhoto[]
  isLoading: boolean
  error: Error | null
} => {
  const { authorizedAxios } = useKestrel()
  const { ROLODEX_URL } = useRuntimeVariables()

  const {
    fileRefs: thumbnailFileRefs,
    isLoading: thumbnailsLoading,
    error,
  } = useRolodexFileLS(
    entryId
      ? {
          entry_id: entryId,
          path: '/photos/thumbnails/**',
          limit: PHOTO_FILE_LIMIT,
        }
      : undefined
  )

  const imageFiles = useMemo(
    () => extractImageFiles(thumbnailFileRefs),
    [thumbnailFileRefs]
  )

  const photoManager = useMemo(() => {
    if (!entryId || !authorizedAxios || !ROLODEX_URL) return null

    const imageNames = imageFiles.map((file) => file.filename)
    return new PhotoManager({
      entryID: entryId,
      imageNames,
      axios: authorizedAxios,
      rolodexURL: ROLODEX_URL,
    })
  }, [entryId, imageFiles, authorizedAxios, ROLODEX_URL])

  const photos = useMemo(() => {
    if (!photoManager) return []

    return imageFiles.map((file) => ({
      entryId: file.entry_id,
      contentType: file.content_type,
      path: file.path,
      filename: file.filename,
      id: `${file.entry_id}_${file.path}`,
      getThumbnailUrl: () =>
        photoManager.fetchPhoto(file.filename, 'thumbnails'),
      getFullSizeUrl: () => photoManager.fetchPhoto(file.filename, 'raw'),
      getSam2Url: () => photoManager.fetchPhoto(file.filename, 'sam2'),
      getSiglipUrl: () => photoManager.fetchPhoto(file.filename, 'siglip'),
    }))
  }, [imageFiles, photoManager])

  return {
    photos,
    isLoading: thumbnailsLoading,
    error,
  }
}
